/**
 * Per-step right-side drawer.
 *
 * Spec: dev/projects/sl2-sstv2-live-trace.md §4.3
 *
 * Sections (top → bottom): Header, Timing, Resolved context, Input, Output,
 * Effects, Error, CloudWatch deep-link.
 *
 * Render-light: drawer stays mounted (open/close via the `open` prop) so MUI
 * can animate. We keep a hard 480px width per the spec.
 */
import { ExpandMore, OpenInNew } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { JsonView } from "react-json-view-lite"
import { Link as RouterLink } from "react-router-dom"

import "react-json-view-lite/dist/index.css"

import { buildCloudWatchInsightsUrl } from "./cloudwatchUrl"
import { fmtAbsolute, fmtBytes, fmtDuration } from "./formatters"
import Sstv2TraceEffectsTable from "./Sstv2TraceEffectsTable"
import {
  getKindStyle,
  getMuiStepStatusColor,
} from "./traceColors"
import type { TraceStepItem } from "./types"

type Props = {
  open: boolean
  step?: TraceStepItem
  traceId: string
  onClose: () => void
}

const SectionHeader: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "text.secondary",
      display: "block",
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
)

const KV: FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <Stack direction="row" spacing={2} sx={{ mb: 0.5 }}>
    <Typography
      variant="caption"
      sx={{ minWidth: 100, color: "text.secondary" }}
    >
      {k}
    </Typography>
    <Box sx={{ fontSize: 12, fontFamily: "monospace", flex: 1, wordBreak: "break-word" }}>
      {v}
    </Box>
  </Stack>
)

const TruncationChip: FC<{ originalSize?: number }> = ({ originalSize }) => (
  <Chip
    size="small"
    color="warning"
    label={`truncated · original ${fmtBytes(originalSize)}`}
    sx={{ ml: 1 }}
  />
)

export const Sstv2TraceStepDrawer: FC<Props> = ({
  open,
  step,
  traceId,
  onClose,
}) => {
  const kindStyle = step ? getKindStyle(step.kind) : null
  const cwUrl = step ? buildCloudWatchInsightsUrl(traceId) : ""

  return (
    <Drawer data-ui="UI-SL2W-247"
      anchor="right"
      open={open}
      onClose={onClose}
      // Position the drawer's Paper just below the app bar so the bell /
      // avatar stay visible while the drawer is open. MUI's
      // `theme.mixins.toolbar.minHeight` gives the standard 64px (desktop) /
      // 56px (mobile) — the same value AppBar uses, so the drawer's top edge
      // aligns with the app bar's bottom.
      PaperProps={{
        sx: {
          width: 480,
          maxWidth: "90vw",
          top: { xs: 56, sm: 64 },
          height: { xs: "calc(100% - 56px)", sm: "calc(100% - 64px)" },
        },
      }}
    >
      {!step ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2">No step selected.</Typography>
        </Box>
      ) : (
        <Box sx={{ p: 2.5 }}>
          {/* Header */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flex: 1, fontFamily: "monospace" }}>
              {step.stepId}
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <Typography variant="body2">×</Typography>
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {kindStyle && (
              <Chip
                size="small"
                label={kindStyle.label}
                sx={{
                  backgroundColor: kindStyle.background,
                  color: kindStyle.color,
                  fontWeight: 600,
                }}
              />
            )}
            <Chip
              size="small"
              label={step.status}
              color={getMuiStepStatusColor(step.status)}
            />
            <Chip size="small" label={`#${step.stepSequence}`} variant="outlined" />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {/* Timing */}
          <Box sx={{ mb: 2 }}>
            <SectionHeader>Timing</SectionHeader>
            <KV k="Started" v={fmtAbsolute(step.startedAt)} />
            <KV
              k="Completed"
              v={step.completedAt ? fmtAbsolute(step.completedAt) : "(in progress)"}
            />
            <KV k="Duration" v={fmtDuration(step.durationMs)} />
          </Box>

          {/* Resolved context */}
          {(step.tenantId || step.reservationId || step.lockerId) && (
            <Box sx={{ mb: 2 }}>
              <SectionHeader>Resolved context</SectionHeader>
              {step.tenantId && (
                <KV
                  k="Tenant"
                  v={
                    <Link
                      component={RouterLink}
                      to={`/sa/tenants/${encodeURIComponent(step.tenantId)}`}
                    >
                      {step.tenantId}
                    </Link>
                  }
                />
              )}
              {step.reservationId && (
                <KV
                  k="Reservation"
                  v={
                    <Link
                      component={RouterLink}
                      to={`/os/reservations/${encodeURIComponent(step.reservationId)}`}
                    >
                      {step.reservationId}
                    </Link>
                  }
                />
              )}
              {step.lockerId && (
                <KV
                  k="Locker"
                  v={
                    <Link
                      component={RouterLink}
                      to={`/sa/sstv2/lockers/${encodeURIComponent(step.lockerId)}`}
                    >
                      {step.lockerId}
                    </Link>
                  }
                />
              )}
            </Box>
          )}

          {/* Input */}
          <Accordion defaultExpanded={false} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2">
                Input
                {step.inputTruncated && (
                  <TruncationChip originalSize={step.inputOriginalSize} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {step.input == null ? (
                <Typography variant="caption" color="text.secondary">
                  (none)
                </Typography>
              ) : (
                <Box sx={{ fontSize: 12 }}>
                  <JsonView data={step.input as object} />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Output */}
          <Accordion defaultExpanded={false} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2">
                Output
                {step.outputTruncated && (
                  <TruncationChip originalSize={step.outputOriginalSize} />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {step.output == null ? (
                <Typography variant="caption" color="text.secondary">
                  (none)
                </Typography>
              ) : (
                <Box sx={{ fontSize: 12 }}>
                  <JsonView data={step.output as object} />
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Effects */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <SectionHeader>Effects</SectionHeader>
            <Sstv2TraceEffectsTable effects={step.effects} />
          </Box>

          {/* Error */}
          {step.error && (
            <Accordion defaultExpanded disableGutters>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography variant="body2" color="error">
                    Error: {step.error.name}
                  </Typography>
                  {step.error.statusCode != null && (
                    <Chip
                      size="small"
                      color={step.error.statusCode >= 500 ? "error" : "warning"}
                      label={`HTTP ${step.error.statusCode}`}
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {step.error.message}
                </Typography>
                {step.error.path && (
                  <KV k="Path" v={step.error.path} />
                )}
                {step.error.responseBody && (
                  <Box sx={{ mb: 1 }}>
                    <SectionHeader>Response body</SectionHeader>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "rgba(211, 47, 47, 0.06)",
                        p: 1,
                        borderRadius: 1,
                        fontSize: 11,
                        overflow: "auto",
                        maxHeight: 240,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {step.error.responseBody}
                    </Box>
                  </Box>
                )}
                {step.error.stack && (
                  <Box sx={{ mb: 0.5 }}>
                    <SectionHeader>Stack</SectionHeader>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: "rgba(0, 0, 0, 0.04)",
                        p: 1,
                        borderRadius: 1,
                        fontSize: 11,
                        overflow: "auto",
                        maxHeight: 240,
                      }}
                    >
                      {step.error.stack}
                    </Box>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}

          <Divider sx={{ my: 2 }} />

          {/* CloudWatch deep-link */}
          {cwUrl && (
            <Link
              href={cwUrl}
              target="_blank"
              rel="noreferrer noopener"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 13,
              }}
            >
              Open in CloudWatch Logs Insights
              <OpenInNew fontSize="inherit" />
            </Link>
          )}
        </Box>
      )}
    </Drawer>
  )
}

export default Sstv2TraceStepDrawer
